import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  Autocomplete,
  TextField,
  Button,
  Chip,
  Stack
} from "@mui/material";
import TuneIcon from '@mui/icons-material/Tune';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {toSnakeCase} from "../utils/general_utils";

const textFieldStyles = { width: '100%' };

export default function InputChipSelect(props) {
  const [itemName] = useState(props.item);
  const [rows, setRows] = useState(props.data);

  // ⬇️ selectedValues siempre como strings
  const [selectedValues, setSelectedValues] = useState(
    (props.value || []).map(v => (v == null ? "" : String(v)))
  );

  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const icon = props.icon ? props.icon : <TuneIcon />;

  useEffect(() => {
    // 1) Base: props.opt o map de data
    const raw = (props.opt ?? props.data.map(item => item?.[itemName]));

    // 2) Saneamos: sin null/undefined/"" y todo a string
    const cleaned = raw
      .filter(v => v != null && v !== "")
      .map(v => (typeof v === "string" ? v : String(v)));

    // 3) Únicos
    const unique = Array.from(new Set(cleaned));

    // 4) No mostrar los ya seleccionados
    const filtered = unique.filter(val => !selectedValues.includes(val));

    setOptions(filtered);
    setRows(props.data);
  }, [props.data, props.opt, itemName, selectedValues]);

  const handleAddClick = () => {
    const valueToAdd = selectedOption == null ? null : String(selectedOption);
    if (valueToAdd && !selectedValues.includes(valueToAdd)) {
      const newValues = [...selectedValues, valueToAdd];
      setSelectedValues(newValues);
      props.callback?.(itemName, newValues);
    }
    // limpiar controles
    setSelectedOption(null);
    setInputValue('');
  };

  const handleClearClick = () => {
    setSelectedValues([]);
    props.callback?.(itemName, []);
  };

  const handleDeleteChip = (chipToDelete) => {
    const newValues = selectedValues.filter(value => value !== chipToDelete);
    setSelectedValues(newValues);
    props.callback?.(itemName, newValues);
  };

  return (
    <Box sx={{borderLeft: "1px solid #B4B4B4", pl: 1, pb: 1}} >
      <Stack direction="row" spacing={1} alignItems="center">
        <FormControl sx={{ flexGrow: 1, minHeight: '36px' }} size="small">
          <Autocomplete
            test_id={toSnakeCase("Autocomplete_"+props.name)}
            sx={{
              padding: 0,
              minHeight: '28px',
              '& .MuiInputBase-root': { minHeight: '32px', height: '32px', fontSize: '0.75rem', paddingRight: '8px' },
              '& .MuiOutlinedInput-input': { padding: '4px 8px' },
              '& .MuiAutocomplete-endAdornment': { top: 'calc(50% - 0px)' },
            }}
            size="small"
            value={selectedOption}                  // puede ser null
            onChange={(event, newValue) => setSelectedOption(newValue)}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
            id="chip-select-autocomplete"
            options={options}
            getOptionLabel={(opt) => (opt == null ? "" : String(opt))}
            isOptionEqualToValue={(opt, val) => String(opt) === String(val)}
            renderInput={(params) => (
              <TextField
                test_id={toSnakeCase(props.name)}
                {...params}
                label={props.name}
                InputLabelProps={{
                  shrink: isFocused || inputValue.length > 0,
                  sx: { marginLeft: inputValue.length > 0 || isFocused ? 0 : "28px", fontSize: '0.75rem' },
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                      {icon}
                      {params.InputProps.startAdornment}
                    </Box>
                  ),
                }}
              />
            )}
          />
        </FormControl>

        <Button
          test_id={toSnakeCase("Agregar a la lista")}
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleAddClick}
          size="small"
          sx={{ color: "#1976D2", whiteSpace: "nowrap" }}
        >
          Agregar a la lista
        </Button>
        <Button
          test_id={toSnakeCase("Limpiar filtro")}
          startIcon={<RemoveCircleOutlineIcon />}
          onClick={handleClearClick}
          size="small"
          sx={{ color: "#D84315", whiteSpace: "nowrap" }}
        >
          Limpiar filtro
        </Button>
      </Stack>

      <Box
        sx={{
          border: "1px solid #999",
          mt: 0.5,
          px: 1,
          py: 0.2,
          borderRadius: 1.5,
          display: "flex",
          flexWrap: "wrap",
          gap: 0,
          minHeight: "32px",
          alignItems: "center"
        }}
      >
        {selectedValues.length > 0 ? (
          selectedValues.map((value, index) => (
            <Chip
              test_id={toSnakeCase(value)}
              key={index}
              label={value}
              onDelete={() => handleDeleteChip(value)}
              size="small"
              sx={{ fontSize: 13 }}
            />
          ))
        ) : (
          <Box sx={{ color: '#888', fontSize: 13 }}>Sin elementos seleccionados</Box>
        )}
      </Box>
    </Box>
  );
}
